import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  InputDate,
} from '@/components/_common';
import { useInvoiceAllXml } from '@/core/domain/invoiceTransmissionHub/AuxiliaryDocuments/AuxiliaryDocuments.hooks';
import { useDefaultEmptyFieldError, useReportGenerate } from '@/hooks';
import { Alert, Col, Form, Modal, Row } from 'antd';
import { Moment } from 'moment';
import { useCallback } from 'react';

interface Props {
  opened: boolean;
  onClose: VoidFunction;
}

interface FormValues {
  companyId: number;
  startDate: Moment;
  endDate: Moment;
}

export default function XMLDownloadModal(props: Props) {
  const [form] = Form.useForm<FormValues>();
  const startDate = Form.useWatch('startDate', form);
  const endDate = Form.useWatch('endDate', form);

  const required = useDefaultEmptyFieldError();

  const invoiceAllXml = useInvoiceAllXml();

  const isLoading = invoiceAllXml.isLoading;

  const report = useReportGenerate();

  const handleClose = () => {
    if (isLoading) return;

    form.resetFields();
    props.onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const params = {
        companyId: values.companyId,
        startDate: values.startDate.format('YYYY-MM-DD'),
        endDate: values.endDate.format('YYYY-MM-DD'),
      };

      const result = await invoiceAllXml.mutateAsync(params);

      if (result) {
        report.downloadFile({
          fileName: `invoices-XML_${params.startDate}_${params.endDate}`,
          result,
          type: 'application/zip',
        });
      }

      handleClose();
    } catch {}
  };

  const disabledStartDate = useCallback(
    (date: Moment) => {
      if (!endDate) return false;

      const maxStartDate = endDate.clone().subtract(1, 'year');

      return date.isAfter(endDate, 'day') || date.isBefore(maxStartDate, 'day');
    },
    [startDate, endDate],
  );

  const disabledEndDate = useCallback(
    (date: Moment) => {
      if (!startDate) return false;

      const maxEndDate = startDate.clone().add(1, 'year');

      return date.isBefore(startDate, 'day') || date.isAfter(maxEndDate, 'day');
    },
    [startDate, endDate],
  );

  return (
    <Modal
      title="Baixar arquivos XMLs"
      open={props.opened}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText="Baixar XMLs"
      confirmLoading={isLoading}
      cancelButtonProps={{ disabled: isLoading }}
    >
      <Form form={form} layout="vertical">
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <FloatingFormItem
              name="companyId"
              label="Empresa"
              rules={[required]}
            >
              <CompaniesInfiniteSelectField />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12}>
            <FloatingFormItem
              name="startDate"
              label="Data inicial"
              rules={[
                required,
                ({ getFieldValue }) => ({
                  validator(_, value: Moment) {
                    const endDateField = getFieldValue('endDate')?.clone();

                    if (
                      !!value &&
                      !!endDateField &&
                      value.clone().isBefore(endDateField?.subtract(1, 'year'))
                    ) {
                      return Promise.reject(new Error('Limite de 1 ano'));
                    }
                    form.setFields([{ name: 'endDate', errors: [] }]);
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputDate allowClear disabledDate={disabledStartDate} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12}>
            <FloatingFormItem
              name="endDate"
              label="Data final"
              rules={[
                required,
                ({ getFieldValue }) => ({
                  validator(_, value: Moment) {
                    const startDateField = getFieldValue('startDate').clone();
                    if (
                      !!value &&
                      !!startDateField &&
                      value.clone().isAfter(startDateField?.add(1, 'year'))
                    ) {
                      return Promise.reject(new Error('Limite de 1 ano'));
                    }
                    form.setFields([{ name: 'startDate', errors: [] }]);
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputDate allowClear disabledDate={disabledEndDate} />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <Alert
              type="warning"
              description="Limite de período de seleção das datas de 1 ano."
            />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
